import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../components/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { UserPlus, KeyRound, ShieldOff, ShieldCheck, RotateCcw, Users, LockKeyhole, Unlock, Clock } from "lucide-react";
import StoreAccessCodes from "../components/StoreAccessCodes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin Users — Super-Admin-only account management.
 * Create admin accounts, set/reset passwords, activate/deactivate, reset 2FA.
 */
export default function AdminUsers() {
  const { isSuperAdmin, masterCode, sessionToken, adminHeaders, checking } = useAuth();

  const authHeaders = useCallback(() => {
    const h = {};
    if (masterCode) h["x-master-code"] = masterCode;
    if (sessionToken) h["x-session-token"] = sessionToken;
    return h;
  }, [masterCode, sessionToken]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ username: "", name: "", tier: "area", password: "" });
  const [creating, setCreating] = useState(false);
  const [pwById, setPwById] = useState({});
  const [traineePw, setTraineePw] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { headers: authHeaders() });
      setUsers(data.users || []);
    } catch {
      toast.error("Could not load admin users");
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { if (isSuperAdmin && masterCode) load(); }, [isSuperAdmin, masterCode, load]);

  const loadTraineeInfo = useCallback(async () => {
    try { await axios.get(`${API}/training/auth-info`, { headers: authHeaders() }); } catch {}
  }, [authHeaders]);
  useEffect(() => { if (isSuperAdmin && masterCode) loadTraineeInfo(); }, [isSuperAdmin, masterCode, loadTraineeInfo]);

  const saveTraineePw = async () => {
    if (!traineePw || traineePw.length < 4) { toast.error("Password must be at least 4 characters"); return; }
    try {
      await axios.post(`${API}/training/password`, { password: traineePw }, { headers: authHeaders() });
      toast.success("Trainee password updated");
      setTraineePw("");
    } catch { toast.error("Could not update trainee password"); }
  };

  // ── Locked accounts (auto-refreshing) ──────────────────────────────────────
  const [locked, setLocked] = useState([]);
  const [nowTs, setNowTs] = useState(Date.now());
  const loadLocked = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/locked-accounts`, { headers: authHeaders() });
      setLocked(data.accounts || []);
    } catch {}
  }, [authHeaders]);
  useEffect(() => {
    if (!isSuperAdmin || !masterCode) return undefined;
    loadLocked();
    const refresh = setInterval(loadLocked, 5000);
    const tick = setInterval(() => setNowTs(Date.now()), 1000);
    return () => { clearInterval(refresh); clearInterval(tick); };
  }, [isSuperAdmin, masterCode, loadLocked]);
  const unlockAcct = async (identifier) => {
    try {
      await axios.post(`${API}/admin/unlock-account`, { identifier }, { headers: authHeaders() });
      toast.success("Account unlocked");
      loadLocked();
    } catch { toast.error("Could not unlock account"); }
  };
  const fmtCountdown = (s) => (s >= 60 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}` : `${s}s`);
  const activeLocked = locked
    .map((a) => ({ ...a, remaining: Math.max(0, Math.ceil((Date.parse(a.unlock_at) - nowTs) / 1000)) }))
    .filter((a) => a.remaining > 0);

  if (checking) return null;
  if (!isSuperAdmin) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="admin-users-denied">
        <p className="eyebrow mb-2">Restricted</p>
        <h1 className="text-2xl font-bold text-[#1d1d1f]">Super Admin access only</h1>
        <p className="text-[#6e6e73] mt-2">Account management is available to Super Admins.</p>
      </div>
    );
  }

  const create = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) { toast.error("Username and password are required"); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/users`, form, { headers: adminHeaders() });
      toast.success(`Account '${form.username}' created`);
      setForm({ username: "", name: "", tier: "area", password: "" });
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not create account");
    } finally {
      setCreating(false);
    }
  };

  const resetPassword = async (u) => {
    const pw = pwById[u.id];
    if (!pw || pw.length < 6) { toast.error("Enter a new password (min 6 chars)"); return; }
    try {
      await axios.post(`${API}/admin/users/${u.id}/password`, { password: pw }, { headers: adminHeaders() });
      toast.success(`Password updated for ${u.username}`);
      setPwById((m) => ({ ...m, [u.id]: "" }));
      load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not update password");
    }
  };

  const toggleActive = async (u) => {
    try {
      await axios.patch(`${API}/admin/users/${u.id}`, { active: !u.active }, { headers: adminHeaders() });
      toast.success(`${u.username} ${u.active ? "deactivated" : "activated"}`);
      load();
    } catch { toast.error("Could not update account"); }
  };

  const reset2fa = async (u) => {
    if (!window.confirm(`Reset 2FA for ${u.username}? They'll re-enrol their authenticator on next login.`)) return;
    try {
      await axios.post(`${API}/admin/users/${u.id}/reset-2fa`, null, { headers: adminHeaders() });
      toast.success(`2FA reset for ${u.username}`);
      load();
    } catch { toast.error("Could not reset 2FA"); }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-10" data-testid="admin-users-page">
      <h1 className="text-3xl font-bold tracking-tight text-[#1d1d1f] flex items-center gap-2">
        <Users size={26} className="text-[#bf6800]" /> Users &amp; Logins
      </h1>
      <p className="text-[#6e6e73] mt-1 mb-8">Everyone signs in with a username + password on one screen. Manage back-office accounts, store logins, the shared Trainee login and locked accounts here.</p>

      <p className="eyebrow mb-2">Back Office Accounts</p>

      {/* Create */}
      <form onSubmit={create} className="rounded-xl bg-[#f5f5f7] border border-black/[0.1] p-5 mb-8" data-testid="create-user-form">
        <h2 className="text-base font-semibold text-[#1d1d1f] flex items-center gap-2 mb-4"><UserPlus size={18} className="text-[#0071e3]" /> New account</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input data-testid="new-username" placeholder="Username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="bg-white border-black/[0.12] rounded-lg" />
          <Input data-testid="new-name" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-black/[0.12] rounded-lg" />
          <select data-testid="new-tier" value={form.tier} onChange={(e) => setForm({ ...form, tier: e.target.value })} className="bg-white border border-black/[0.12] rounded-lg px-3 h-10 text-sm text-[#1d1d1f]">
            <option value="area">Area Manager</option>
            <option value="super">Super Admin</option>
          </select>
          <Input data-testid="new-password" type="password" placeholder="Temp password (min 6)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="bg-white border-black/[0.12] rounded-lg" />
        </div>
        <Button type="submit" disabled={creating} data-testid="create-user-btn" className="mt-4 bg-[#0071e3] hover:bg-[#0060c0] text-white rounded-full px-6">
          {creating ? "Creating…" : "Create account"}
        </Button>
      </form>

      {/* List */}
      {loading ? (
        <p className="text-[#6e6e73]">Loading…</p>
      ) : (
        <div className="space-y-3" data-testid="admin-users-list">
          {users.map((u) => (
            <div key={u.id} className="rounded-xl bg-white border border-black/[0.1] p-4" data-testid={`user-row-${u.username}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#1d1d1f]">{u.username}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#bf6800]/10 text-[#bf6800]">{u.tier === "super" ? "Super Admin" : "Area Manager"}</span>
                    {!u.active && <span className="text-xs px-2 py-0.5 rounded-full bg-[rgba(200,40,40,0.1)] text-[rgba(200,40,40,0.9)]">Inactive</span>}
                  </div>
                  <p className="text-sm text-[#6e6e73]">{u.name}</p>
                  <p className="text-xs text-[#86868b] mt-0.5">
                    {u.has_password ? "Password set" : "No password yet"} · {u.totp_enrolled ? "2FA enrolled" : "2FA not set up"}
                  </p>
                  <p className="text-xs text-[#86868b] mt-0.5" data-testid={`last-login-${u.username}`}>
                    Last login: {u.last_login ? new Date(u.last_login).toLocaleString() : "Never"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggleActive(u)} data-testid={`toggle-active-${u.username}`} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full border border-black/[0.12] text-[#1d1d1f] hover:bg-[#f5f5f7]">
                    {u.active ? <><ShieldOff size={14} /> Deactivate</> : <><ShieldCheck size={14} /> Activate</>}
                  </button>
                  <button onClick={() => reset2fa(u)} data-testid={`reset-2fa-${u.username}`} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full border border-black/[0.12] text-[#1d1d1f] hover:bg-[#f5f5f7]">
                    <RotateCcw size={14} /> Reset 2FA
                  </button>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <KeyRound size={15} className="text-[#0071e3]" />
                <Input type="password" placeholder="Set new password" value={pwById[u.id] || ""} onChange={(e) => setPwById((m) => ({ ...m, [u.id]: e.target.value }))} data-testid={`pw-input-${u.username}`} className="bg-white border-black/[0.12] rounded-lg h-9 max-w-xs" />
                <Button onClick={() => resetPassword(u)} data-testid={`set-pw-${u.username}`} className="bg-[#1d1d1f] hover:bg-black text-white rounded-full h-9 px-4 text-xs">Set password</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-10" data-testid="locked-accounts-panel">
        <p className="eyebrow mb-2">Locked Accounts</p>
        <div className="rounded-xl bg-white border border-black/[0.1] p-4">
          <div className="flex items-center gap-2 mb-3">
            <LockKeyhole size={16} className="text-[#bf6800]" />
            <span className="font-semibold text-[#1d1d1f]">Temporarily locked logins</span>
            <span className="text-[11px] text-[#86868b] ml-auto flex items-center gap-1"><Clock size={12} /> auto-refreshes</span>
          </div>
          {activeLocked.length === 0 ? (
            <p className="text-sm text-[#6e6e73]" data-testid="no-locked-accounts">No accounts are locked right now. Logins lock for 15 minutes after 5 failed attempts.</p>
          ) : (
            <div className="space-y-2" data-testid="locked-accounts-list">
              {activeLocked.map((a) => (
                <div key={a.identifier} data-testid={`locked-row-${a.identifier}`} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-black/[0.08] bg-[#fbfbfd] px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#1d1d1f] truncate">{a.account}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full ${a.lock_type === "hard" ? "bg-[rgba(200,40,40,0.1)] text-[rgba(200,40,40,0.9)]" : "bg-[#bf6800]/10 text-[#bf6800]"}`}>
                        {a.lock_type === "hard" ? "Locked" : "Cooling down"}
                      </span>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#f0f0f2] text-[#6e6e73]">{a.kind === "login" ? "Login" : a.kind === "admin" ? "Admin" : "Store PIN"}</span>
                    </div>
                    <p className="text-xs text-[#86868b] mt-0.5">
                      {a.attempts} failed attempt{a.attempts !== 1 ? "s" : ""}
                      {a.locked_at ? ` · since ${new Date(a.locked_at).toLocaleTimeString()}` : ""}
                      {" · unlocks in "}
                      <span className="font-semibold text-[#bf6800] tabular-nums" data-testid={`locked-countdown-${a.identifier}`}>{fmtCountdown(a.remaining)}</span>
                    </p>
                  </div>
                  <button onClick={() => unlockAcct(a.identifier)} data-testid={`unlock-${a.identifier}`} className="text-xs flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#0071e3] hover:bg-[#0060c0] text-white font-medium">
                    <Unlock size={14} /> Unlock now
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-10" data-testid="admin-trainee-access">
        <p className="eyebrow mb-2">Staff Training Login</p>
        <div className="rounded-xl bg-white border border-black/[0.1] p-4">
          <div className="flex items-center gap-2 mb-1"><Users size={16} className="text-[#bf6800]" /><span className="font-semibold text-[#1d1d1f]">Trainee (shared account)</span></div>
          <p className="text-sm text-[#6e6e73]">Staff type username <b>Trainee</b> + this password on the normal login screen to reach the Training hub. Current password: <b className="font-mono" data-testid="trainee-current-pw">•••••• (hidden)</b> — set a new one below to change it.</p>
          <div className="flex items-center gap-2 mt-3">
            <KeyRound size={15} className="text-[#0071e3]" />
            <Input type="text" placeholder="New training password" value={traineePw} onChange={(e) => setTraineePw(e.target.value)} data-testid="trainee-pw-input" className="bg-white border-black/[0.12] rounded-lg h-9 max-w-xs" />
            <Button onClick={saveTraineePw} data-testid="set-trainee-pw" className="bg-[#1d1d1f] hover:bg-black text-white rounded-full h-9 px-4 text-xs">Update password</Button>
          </div>
        </div>
      </div>

      <div className="mt-10" data-testid="admin-users-store-codes">
        <p className="eyebrow mb-2">Store Logins</p>
        <StoreAccessCodes />
      </div>
    </div>
  );
}
